import React, { createContext, FC, ReactElement, ReactPortal, useContext, useEffect, useState } from "react";
import { Room, RoomMessage, RoomMessageStatus, RoomMessageType } from "../proto/Global_pb";
import { FileStatus, MediaWrapperContextType } from "../constants/globals";
import { downloadedFiles, FileGet, FileSend, idb } from "../libs/fileHandler";
import { includes, toNumber } from "lodash";
import { FileDownload } from "../proto/FileDownload_pb";
import { downloadURL, formatBytes } from "../libs/file";
import { messageTypeHelper } from "../libs/utils";
import store from "../store";
import { removeFakeMessage } from "../store/messages/actions";

type Props = {
  children: ReactPortal | ReactElement;
  attachment?: RoomMessage.AsObject["attachment"];
  messageType?: RoomMessage.AsObject["messageType"];
  messageStatus?: RoomMessage.AsObject["status"];
  isExternalDownload?: boolean;
  isVideo?: boolean;
  roomId?: Room.AsObject["id"];
  randomId?: RoomMessage.AsObject["randomId"];
  messageId?: RoomMessage.AsObject["messageId"];
};

const MediaWrapperContext = createContext<MediaWrapperContextType | undefined>(undefined);
MediaWrapperContext.displayName = "MediaContext";

export const MediaWrapperProvider: FC<Props> = (props) => {
  const isUploading = Boolean(props.messageStatus === RoomMessageStatus.SENDING);
  const [fileUrl, setFileUrl] = useState<string | undefined>(downloadedFiles[`${props.attachment?.token}`]);
  const [inCache, setInCache] = useState(false);
  const [progress, setProgress] = useState<number | undefined>();
  const [fileStatus, setFileStatus] = useState<FileStatus>(isUploading ? FileStatus.Canceled : FileStatus.DEFAULT);
  const [thumbnail, setThumbnail] = useState<string>("");
  const fileInstance =
    props.attachment && (isUploading ? FileSend.instances[FileSend.attachmentToken_instance[props.attachment.token]] : new FileGet(props.attachment, FileDownload.Selector.FILE, !!props.isVideo));
  const fileSize = formatBytes(toNumber(props.attachment?.size));
  const checkCache = async () => {
    let isInCache = !messageTypeHelper(props.messageType).isVideo() ? await idb.getItem(props.attachment!.token) : await fileInstance?.checkFileDownloaded();
    idb.getItem(props.attachment!.token).then((response) => {
      if (response) {
        setFileUrl(URL.createObjectURL(response as Blob));
      }
    });

    if (!!isInCache && !isUploading) {
      setInCache(true);
      setProgress(100);
      setFileStatus(FileStatus.DOWNLOADED);
      return;
    }
    fileInstance?.onProgress(setProgress);
  };

  useEffect(() => {
    if (props.attachment) {
      checkCache().then();
      if (isUploading) return setFileStatus(FileStatus.Uploading);
      if (
        messageTypeHelper(props.messageType).isImage() ||
        messageTypeHelper(props.messageType).isVideo() ||
        messageTypeHelper(props.messageType).isStory() ||
        messageTypeHelper(props.messageType).isGif()
      ) {
        const thumbnailInstance = new FileGet(
          props.attachment,
          includes([RoomMessageType.GIF, RoomMessageType.GIF_TEXT, RoomMessageType.STORY_REPLY], props.messageType) || props.attachment.mime === "image/webp"
            ? FileDownload.Selector.FILE
            : FileDownload.Selector.LARGE_THUMBNAIL
        );
        thumbnailInstance.onFinish(setThumbnail);
        thumbnailInstance.start();
      }
    }
  }, [props.attachment]);

  const download = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      setFileStatus(FileStatus.Downloading);
      fileInstance?.onFinish((url) => {
        setFileStatus(FileStatus.DOWNLOADED);
        if (props.isExternalDownload) downloadURL(url, props?.attachment!.name);
        setInCache(true);
        setFileUrl(url);
        resolve();
      });
      fileInstance?.onProgress(setProgress);
      fileInstance?.start();
    });
  };

  const cancelDownload = () => {
    setFileStatus(FileStatus.Canceled);
    fileInstance?.cancel();
  };

  const cancelUpload = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const roomId = props.roomId,
      randomId = props.randomId;
    if (fileInstance instanceof FileSend && props.attachment && roomId !== undefined && randomId !== undefined) {
      fileInstance?.cancel();
      fileInstance.clearInstance();
      idb.removeItem(props.attachment.token).then();
      store.dispatch(removeFakeMessage({ roomId, randomId }));
      setFileStatus(FileStatus.Canceled);
    }
  };

  const pauseUpload = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    fileInstance?.pause();
    setFileStatus(FileStatus.PAUSED);
  };

  const continueUpload = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    fileInstance?.start();
    setFileStatus(FileStatus.Uploading);
  };
  const childProps: MediaWrapperContextType = {
    inCache,
    progress: progress || 0,
    fileSize,
    download,
    cancelDownload,
    pauseUpload,
    continueUpload,
    cancelUpload,
    setFileStatus,
    fileStatus,
    fileUrl,
    thumbnail,
    file: props.attachment,
    messageType: props.messageType,
    messageId: props.messageId,
  };

  return <MediaWrapperContext.Provider value={childProps}>{props.children}</MediaWrapperContext.Provider>;
};

export const useMediaWrapper = (): MediaWrapperContextType => {
  const context = useContext(MediaWrapperContext);
  if (context == undefined) throw new Error("");
  return context;
};
